import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const BASE_URL =
  process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || 'http://localhost:3030';
const API_BASE = `${BASE_URL}/api/admin`;

const inputStyle = {
  width: '100%',
  border: '1px solid #D9E2F2',
  borderRadius: '8px',
  padding: '9px 10px',
  background: '#fff',
  fontSize: '13px',
  boxSizing: 'border-box',
};

const buttonStyle = {
  width: '100%',
  border: 'none',
  borderRadius: '10px',
  padding: '12px 14px',
  fontWeight: 700,
  fontSize: '14px',
  color: '#fff',
  background: 'linear-gradient(90deg, #4318FF 0%, #2F80ED 100%)',
  cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(67, 24, 255, 0.25)',
  transition: 'all 0.2s ease',
};

const disabledButtonStyle = {
  ...buttonStyle,
  opacity: 0.6,
  cursor: 'not-allowed',
  boxShadow: 'none',
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function AudioMixer({
  onMixedFile,
  title = 'Audio Mixer',
  disabled = false,
}) {
  const [voiceFile, setVoiceFile] = useState(null);
  const [musicFile, setMusicFile] = useState(null);
  const [musicVolume, setMusicVolume] = useState(35);
  const [mixedFile, setMixedFile] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState('');

  const timerRef = useRef(null);

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('adminToken') ||
      sessionStorage.getItem('token') ||
      '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const mixedUrl = useMemo(
    () => (mixedFile ? URL.createObjectURL(mixedFile) : ''),
    [mixedFile],
  );

  useEffect(() => {
    return () => {
      if (mixedUrl) URL.revokeObjectURL(mixedUrl);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [mixedUrl]);

  const handleGenerate = async () => {
    if (!voiceFile || !musicFile || disabled || isGenerating) return;

    try {
      setError('');
      setIsGenerating(true);
      setElapsedSeconds(0);
      setProgressText('Starting upload to mixing engine...');

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      const formData = new FormData();
      formData.append('voiceAudio', voiceFile);
      formData.append('musicAudio', musicFile);
      formData.append('musicVolume', String(musicVolume));

      const endpoint = `${API_BASE}/mix-audio`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
        timeout: 1800000, // 30 minutes
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            const loadedMB = (progressEvent.loaded / (1024 * 1024)).toFixed(1);
            const totalMB = (progressEvent.total / (1024 * 1024)).toFixed(1);
            if (percent < 100) {
              setProgressText(
                `Uploading audio files (${percent}% — ${loadedMB}MB of ${totalMB}MB)...`,
              );
            } else {
              setProgressText(
                '⚡ Processing & mixing audio with high-speed FFmpeg engine...',
              );
            }
          }
        },
      });

      if (!response?.data) {
        throw new Error('Audio mixing service returned an empty response.');
      }

      const cleanName =
        voiceFile.name.replace(/\.[^.]+$/, '').replace(/[^\w-]+/g, '_') ||
        'voice_audio';
      const outputFileName = `${cleanName}_mixed.mp3`;

      const generatedFile = new File([response.data], outputFileName, {
        type: 'audio/mpeg',
      });

      setMixedFile(generatedFile);
      onMixedFile?.(generatedFile);
      setProgressText('');
    } catch (err) {
      console.error('Audio mix error:', err);
      let errMsg = 'Audio mix generate nahi ho paayi.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errMsg = json.message || errMsg;
        } catch (_) {}
      } else if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      } else if (err.message) {
        errMsg = err.message;
      }
      setError(errMsg);
      setProgressText('');
    } finally {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsGenerating(false);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        border: '1.5px dashed #6C4CFF',
        borderRadius: '14px',
        padding: '16px',
        background: '#F8FAFF',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <span
          style={{
            fontWeight: 800,
            color: '#1B254B',
            fontSize: '14px',
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: '11px',
            background: '#EAE6FF',
            color: '#4318FF',
            padding: '3px 8px',
            borderRadius: '6px',
            fontWeight: 700,
          }}
        >
          ⚡ Server-Powered (Fast FFmpeg Multi-thread)
        </span>
      </div>

      <div style={{ display: 'grid', gap: '12px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
          }}
        >
          <label style={{ display: 'grid', gap: '5px', fontSize: '12px' }}>
            <span style={{ fontWeight: 700, color: '#2D3748' }}>
              Voice Audio{' '}
              {voiceFile && (
                <span style={{ color: '#4318FF', fontWeight: 600 }}>
                  ({formatFileSize(voiceFile.size)})
                </span>
              )}
            </span>
            <input
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
              disabled={disabled || isGenerating}
              style={inputStyle}
              onChange={(e) => {
                setMixedFile(null);
                setVoiceFile(e.target.files?.[0] || null);
              }}
            />
          </label>

          <label style={{ display: 'grid', gap: '5px', fontSize: '12px' }}>
            <span style={{ fontWeight: 700, color: '#2D3748' }}>
              Background Music{' '}
              {musicFile && (
                <span style={{ color: '#4318FF', fontWeight: 600 }}>
                  ({formatFileSize(musicFile.size)})
                </span>
              )}
            </span>
            <input
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
              disabled={disabled || isGenerating}
              style={inputStyle}
              onChange={(e) => {
                setMixedFile(null);
                setMusicFile(e.target.files?.[0] || null);
              }}
            />
          </label>
        </div>

        <label style={{ display: 'grid', gap: '6px', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: '#2D3748' }}>
              Background Volume
            </span>
            <span style={{ fontWeight: 700, color: '#4318FF' }}>
              {musicVolume}%
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={musicVolume}
            disabled={disabled || isGenerating}
            onChange={(e) => setMusicVolume(Number(e.target.value))}
            style={{ width: '100%', accentColor: '#4318FF' }}
          />
        </label>

        <button
          type="button"
          style={
            !voiceFile || !musicFile || disabled || isGenerating
              ? disabledButtonStyle
              : buttonStyle
          }
          disabled={!voiceFile || !musicFile || disabled || isGenerating}
          onClick={handleGenerate}
        >
          {isGenerating ? (
            <span>
              ⏳ Mixing Audio ({elapsedSeconds}s)...
            </span>
          ) : (
            '✨ Generate Mix Audio'
          )}
        </button>

        {isGenerating && (
          <div
            style={{
              textAlign: 'center',
              fontSize: '12px',
              color: '#4318FF',
              fontWeight: 600,
              background: '#EEF2FF',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #C7D2FE',
              display: 'grid',
              gap: '4px',
            }}
          >
            <div>{progressText || 'Processing audio...'}</div>
            <div style={{ fontSize: '11px', color: '#6366F1' }}>
              Elapsed Time: {elapsedSeconds}s
            </div>
          </div>
        )}

        {mixedFile && (
          <div
            style={{
              display: 'grid',
              gap: '10px',
              background: '#F0FDF4',
              border: '1px solid #BBF7D0',
              padding: '12px',
              borderRadius: '10px',
            }}
          >
            <div
              style={{
                color: '#15803D',
                fontSize: '13px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <span>
                ✅ Mixed Audio Ready & Auto-Selected (
                {formatFileSize(mixedFile.size)})
              </span>
              <a
                href={mixedUrl}
                download={mixedFile.name}
                style={{
                  background: '#01B574',
                  color: '#fff',
                  padding: '5px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  textDecoration: 'none',
                  fontWeight: 700,
                }}
              >
                ⬇️ Download MP3
              </a>
            </div>
            <audio controls src={mixedUrl} style={{ width: '100%', height: '36px' }} />
          </div>
        )}

        {error && (
          <div
            style={{
              color: '#B91C1C',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
}



